// src/components/ProfileModal.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiUser,
  FiMapPin,
  FiCreditCard,
  FiFileText,
  FiHeart,
  FiSettings,
  FiCheckCircle,
  FiX,
  FiSearch,
  FiShoppingCart,
} from "react-icons/fi";
import Image from "./Image";

// Reusable Input Component
const InputField = ({ label, value, onChange, error, placeholder, type = "text", darkMode, ...props }) => (
  <div className="space-y-1">
    <label className="block text-sm font-medium">{label}</label>
    <input
      type={type}
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-green-500 text-sm transition-all ${error ? "border-red-600" : "border-gray-300 dark:border-gray-600"} ${darkMode ? "bg-gray-700 text-white" : "bg-gray-100 text-gray-900"}`}
      {...props}
    />
    {error && <p className="text-red-600 text-xs">{error}</p>}
  </div>
);

// Reusable Address/Payment Method Item
const ListItem = ({ item, isDefault, onEdit, onRemove, onSetDefault, darkMode, type }) => (
  <div className="flex items-center justify-between text-sm p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
    <div className="flex items-center gap-2">
      {isDefault && <FiCheckCircle className="text-green-500" />}
      <span className="truncate">{item}</span>
    </div>
    <div className="flex gap-2">
      <motion.button
        onClick={onEdit}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        className="text-green-500 hover:text-green-600"
        aria-label={`Edit ${type} ${item}`}
      >
        Edit
      </motion.button>
      <motion.button
        onClick={onRemove}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        className="text-red-600 hover:text-red-700"
        aria-label={`Remove ${type} ${item}`}
      >
        Remove
      </motion.button>
      {!isDefault && (
        <motion.button
          onClick={onSetDefault}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="text-green-500 hover:text-green-600"
          aria-label={`Set ${item} as default ${type}`}
        >
          Set Default
        </motion.button>
      )}
    </div>
  </div>
);

// Error Boundary Component
class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error in ProfileModal:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 text-center">
          <h2 className="text-xl font-bold text-red-600">Something went wrong</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">Please try again or contact support.</p>
          <button
            className="mt-4 bg-green-500 text-white px-4 py-2 rounded-lg"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const ProfileModal = ({ darkMode, setToastMessage, toggleFavouriteDish, favourites = [], isOpen, onClose, restaurantData }) => {
  const navigate = useNavigate();
  const safeNavigate = (path, options = {}) => {
    try {
      if (window.history.length > 1) {
        navigate(path, options);
      } else {
        window.location.assign(path);
      }
    } catch (error) {
      console.error("Navigation error:", error);
      window.location.assign(path);
    }
  };

  const [profile, setProfile] = useState(() => {
    const defaultProfile = {
      name: "",
      phone: "",
      email: "",
      avatar: "",
      address: "",
      upi: "",
      orders: [],
      favorites: { restaurants: [], dishes: [] },
      rewardPoints: 0,
      paymentMethods: [],
      deliveryAddresses: [],
      preferences: { notifications: true, dietary: "None", language: "English" },
      defaultAddress: "",
      defaultPaymentMethod: "",
      activityLog: [],
    };

    const savedProfile = localStorage.getItem("profile");
    const user = localStorage.getItem("user");

    if (savedProfile) {
      try {
        const parsedProfile = JSON.parse(savedProfile);
        return {
          ...defaultProfile,
          ...parsedProfile,
          preferences: parsedProfile.preferences || defaultProfile.preferences,
          orders: Array.isArray(parsedProfile.orders) ? parsedProfile.orders : [],
          defaultAddress: parsedProfile.defaultAddress || "",
          defaultPaymentMethod: parsedProfile.defaultPaymentMethod || "",
          favorites: parsedProfile.favorites && typeof parsedProfile.favorites === "object"
            ? { restaurants: Array.isArray(parsedProfile.favorites.restaurants) ? parsedProfile.favorites.restaurants : [], dishes: Array.isArray(parsedProfile.favorites.dishes) ? parsedProfile.favorites.dishes : [] }
            : defaultProfile.favorites,
          activityLog: Array.isArray(parsedProfile.activityLog) ? parsedProfile.activityLog : [],
          deliveryAddresses: Array.isArray(parsedProfile.deliveryAddresses) ? parsedProfile.deliveryAddresses : [],
          paymentMethods: Array.isArray(parsedProfile.paymentMethods) ? parsedProfile.paymentMethods : [],
        };
      } catch (e) {
        console.error("Failed to parse profile from localStorage:", e);
        localStorage.removeItem("profile");
      }
    }

    if (user) {
      try {
        const parsedUser = JSON.parse(user);
        return {
          ...defaultProfile,
          name: parsedUser?.displayName || "",
          email: parsedUser?.email || "",
          avatar: parsedUser?.photoURL || "",
        };
      } catch (e) {
        console.error("Failed to parse user from localStorage:", e);
      }
    }

    return defaultProfile;
  });

  const [expandedSection, setExpandedSection] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [newAddress, setNewAddress] = useState("");
  const [editAddress, setEditAddress] = useState({ address: "", index: null });
  const [newPaymentMethod, setNewPaymentMethod] = useState("");
  const [editPaymentMethod, setEditPaymentMethod] = useState({ method: "", index: null });
  const [notificationToggle, setNotificationToggle] = useState(profile.preferences?.notifications ?? true);
  const [dietaryPreference, setDietaryPreference] = useState(profile.preferences?.dietary ?? "None");
  const [languagePreference, setLanguagePreference] = useState(profile.preferences?.language ?? "English");
  const [editProfile, setEditProfile] = useState({ name: "", phone: "", email: "", upi: "" });
  const [editErrors, setEditErrors] = useState({ name: "", phone: "", email: "", upi: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState({ show: false, action: null, message: "" });

  useEffect(() => {
    if (!Array.isArray(profile.deliveryAddresses) || !Array.isArray(profile.paymentMethods)) {
      setProfile((prev) => ({
        ...prev,
        deliveryAddresses: Array.isArray(prev.deliveryAddresses) ? prev.deliveryAddresses : [],
        paymentMethods: Array.isArray(prev.paymentMethods) ? prev.paymentMethods : [],
      }));
    }
  }, [profile]);

  useEffect(() => {
    try {
      localStorage.setItem("profile", JSON.stringify(profile));
    } catch (error) {
      console.error("Failed to save profile to localStorage:", error);
    }
  }, [profile]);

  useEffect(() => {
    setNotificationToggle(profile.preferences?.notifications ?? true);
    setDietaryPreference(profile.preferences?.dietary ?? "None");
    setLanguagePreference(profile.preferences?.language ?? "English");
  }, [profile]);

  const profileCompletion = useMemo(() => {
    let completed = 0;
    const fields = [
      !!profile.name,
      !!profile.phone,
      !!profile.email,
      !!profile.avatar,
      !!profile.upi,
      Array.isArray(profile.deliveryAddresses) && profile.deliveryAddresses.length > 0,
      Array.isArray(profile.paymentMethods) && profile.paymentMethods.length > 0,
    ];
    fields.forEach((field) => field && completed++);
    return Math.round((completed / fields.length) * 100);
  }, [profile]);

  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploading(true);
      const reader = new FileReader();
      reader.onload = () => {
        setProfile((prev) => ({
          ...prev,
          avatar: reader.result,
          activityLog: [...prev.activityLog, { action: "Updated avatar", timestamp: new Date().toISOString() }],
        }));
        setUploading(false);
        setToastMessage("Avatar updated!");
      };
      reader.onerror = () => {
        console.error("Error reading avatar file");
        setUploading(false);
        setToastMessage("Failed to upload avatar.");
      };
      reader.readAsDataURL(file);
    }
  };

  const confirmAction = (action, message) => {
    setShowConfirm({ show: true, action, message });
  };

  const handleConfirm = () => {
    showConfirm.action();
    setShowConfirm({ show: false, action: null, message: "" });
  };

  const logout = useCallback(() => {
    localStorage.clear();
    sessionStorage.clear();
    setProfile({
      name: "",
      phone: "",
      email: "",
      avatar: "",
      address: "",
      upi: "",
      orders: [],
      favorites: { restaurants: [], dishes: [] },
      rewardPoints: 0,
      paymentMethods: [],
      deliveryAddresses: [],
      preferences: { notifications: true, dietary: "None", language: "English" },
      defaultAddress: "",
      defaultPaymentMethod: "",
      activityLog: [],
    });
    onClose();
    safeNavigate("/login");
    setToastMessage("Signed out successfully.");
  }, [onClose, setToastMessage]);

  const addFavoriteRestaurant = (restaurant) => {
    setProfile((prev) => {
      const isFavorite = prev.favorites.restaurants.includes(restaurant);
      const updatedFavorites = {
        ...prev.favorites,
        restaurants: isFavorite
          ? prev.favorites.restaurants.filter((r) => r !== restaurant)
          : [...prev.favorites.restaurants, restaurant],
      };
      return {
        ...prev,
        favorites: updatedFavorites,
        activityLog: [
          ...prev.activityLog,
          { action: `${isFavorite ? "Removed" : "Added"} ${restaurant} to favorites`, timestamp: new Date().toISOString() },
        ],
      };
    });
    setToastMessage(`${restaurant} ${profile.favorites.restaurants.includes(restaurant) ? "removed from" : "added to"} favorites!`);
  };

  const addFavoriteDish = (dish) => {
    toggleFavouriteDish(dish.id);
    setProfile((prev) => {
      const isFavorite = prev.favorites.dishes.includes(dish.id);
      const updatedFavorites = {
        ...prev.favorites,
        dishes: isFavorite
          ? prev.favorites.dishes.filter((d) => d !== dish.id)
          : [...prev.favorites.dishes, dish.id],
      };
      return {
        ...prev,
        favorites: updatedFavorites,
        activityLog: [
          ...prev.activityLog,
          { action: `${isFavorite ? "Removed" : "Added"} ${dish.name} to favorites`, timestamp: new Date().toISOString() },
        ],
      };
    });
    const isFavourite = favourites.includes(dish.id);
    setToastMessage(`${dish.name} ${isFavourite ? "removed from" : "added to"} favorites!`);
  };

  const reorderDish = (dish) => {
    setToastMessage(`${dish.name} added to cart!`);
    safeNavigate(`/restaurant/${Object.keys(restaurantData).find((key) => restaurantData[key].menu.some((m) => m.id === dish.id))}`);
  };

  const addDeliveryAddress = () => {
    if (newAddress.trim()) {
      setProfile((prev) => ({
        ...prev,
        deliveryAddresses: [...(Array.isArray(prev.deliveryAddresses) ? prev.deliveryAddresses : []), newAddress],
        defaultAddress: prev.defaultAddress || newAddress,
        activityLog: [...prev.activityLog, { action: `Added address: ${newAddress}`, timestamp: new Date().toISOString() }],
      }));
      setNewAddress("");
      setToastMessage("Address added!");
    } else {
      setToastMessage("Please enter a valid address.");
    }
  };

  const editDeliveryAddress = () => {
    if (editAddress.address.trim() && editAddress.index !== null) {
      setProfile((prev) => ({
        ...prev,
        deliveryAddresses: prev.deliveryAddresses.map((addr, i) => (i === editAddress.index ? editAddress.address : addr)),
        defaultAddress: prev.defaultAddress === prev.deliveryAddresses[editAddress.index] ? editAddress.address : prev.defaultAddress,
        activityLog: [...prev.activityLog, { action: `Edited address to: ${editAddress.address}`, timestamp: new Date().toISOString() }],
      }));
      setEditAddress({ address: "", index: null });
      setToastMessage("Address updated!");
    } else {
      setToastMessage("Please enter a valid address.");
    }
  };

  const removeDeliveryAddress = (address) => {
    setProfile((prev) => ({
      ...prev,
      deliveryAddresses: (Array.isArray(prev.deliveryAddresses) ? prev.deliveryAddresses : []).filter((addr) => addr !== address),
      defaultAddress: prev.defaultAddress === address ? "" : prev.defaultAddress,
      activityLog: [...prev.activityLog, { action: `Removed address: ${address}`, timestamp: new Date().toISOString() }],
    }));
    setToastMessage("Address removed!");
  };

  const setDefaultAddress = (address) => {
    setProfile((prev) => ({
      ...prev,
      defaultAddress: address,
      activityLog: [...prev.activityLog, { action: `Set default address: ${address}`, timestamp: new Date().toISOString() }],
    }));
    setToastMessage(`Set ${address} as default address.`);
  };

  const addPaymentMethod = () => {
    if (newPaymentMethod.trim()) {
      setProfile((prev) => ({
        ...prev,
        paymentMethods: [...(Array.isArray(prev.paymentMethods) ? prev.paymentMethods : []), newPaymentMethod],
        defaultPaymentMethod: prev.defaultPaymentMethod || newPaymentMethod,
        activityLog: [...prev.activityLog, { action: `Added payment method: ${newPaymentMethod}`, timestamp: new Date().toISOString() }],
      }));
      setNewPaymentMethod("");
      setToastMessage("Payment method added!");
    } else {
      setToastMessage("Please enter a valid payment method.");
    }
  };

  const editPaymentMethodFn = () => {
    if (editPaymentMethod.method.trim() && editPaymentMethod.index !== null) {
      setProfile((prev) => ({
        ...prev,
        paymentMethods: prev.paymentMethods.map((method, i) => (i === editPaymentMethod.index ? editPaymentMethod.method : method)),
        defaultPaymentMethod: prev.defaultPaymentMethod === prev.paymentMethods[editPaymentMethod.index] ? editPaymentMethod.method : prev.defaultPaymentMethod,
        activityLog: [...prev.activityLog, { action: `Edited payment method to: ${editPaymentMethod.method}`, timestamp: new Date().toISOString() }],
      }));
      setEditPaymentMethod({ method: "", index: null });
      setToastMessage("Payment method updated!");
    } else {
      setToastMessage("Please enter a valid payment method.");
    }
  };

  const removePaymentMethod = (method) => {
    setProfile((prev) => ({
      ...prev,
      paymentMethods: (Array.isArray(prev.paymentMethods) ? prev.paymentMethods : []).filter((m) => m !== method),
      defaultPaymentMethod: prev.defaultPaymentMethod === method ? "" : prev.defaultPaymentMethod,
      activityLog: [...prev.activityLog, { action: `Removed payment method: ${method}`, timestamp: new Date().toISOString() }],
    }));
    setToastMessage("Payment method removed!");
  };

  const setDefaultPaymentMethod = (method) => {
    setProfile((prev) => ({
      ...prev,
      defaultPaymentMethod: method,
      activityLog: [...prev.activityLog, { action: `Set default payment method: ${method}`, timestamp: new Date().toISOString() }],
    }));
    setToastMessage(`Set ${method} as default payment method.`);
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handleNotificationToggle = () => {
    setNotificationToggle((prev) => !prev);
    setProfile((prev) => ({
      ...prev,
      preferences: { ...prev.preferences, notifications: !notificationToggle },
      activityLog: [
        ...prev.activityLog,
        { action: `Notifications ${!notificationToggle ? "enabled" : "disabled"}`, timestamp: new Date().toISOString() },
      ],
    }));
    setToastMessage(`Notifications ${!notificationToggle ? "enabled" : "disabled"}.`);
  };

  const handleDietaryChange = (e) => {
    setDietaryPreference(e.target.value);
    setProfile((prev) => ({
      ...prev,
      preferences: { ...prev.preferences, dietary: e.target.value },
      activityLog: [...prev.activityLog, { action: `Changed dietary preference to: ${e.target.value}`, timestamp: new Date().toISOString() }],
    }));
    setToastMessage(`Dietary preference set to ${e.target.value}.`);
  };

  const handleLanguageChange = (e) => {
    setLanguagePreference(e.target.value);
    setProfile((prev) => ({
      ...prev,
      preferences: { ...prev.preferences, language: e.target.value },
      activityLog: [...prev.activityLog, { action: `Changed language to: ${e.target.value}`, timestamp: new Date().toISOString() }],
    }));
    setToastMessage(`Language set to ${e.target.value}.`);
  };

  const handleEditProfile = () => {
    setEditProfile({
      name: profile.name || "",
      phone: profile.phone || "",
      email: profile.email || "",
      upi: profile.upi || "",
    });
    setIsEditing(true);
  };

  const validateProfileFields = useCallback(() => {
    const errors = { name: "", phone: "", email: "", upi: "" };
    let isValid = true;
    if (!editProfile.name.trim()) {
      errors.name = "Name is required";
      isValid = false;
    }
    if (!editProfile.phone.match(/^\d{10}$/)) {
      errors.phone = "Enter a valid 10-digit phone number";
      isValid = false;
    }
    if (!editProfile.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      errors.email = "Enter a valid email address";
      isValid = false;
    }
    if (editProfile.upi && !editProfile.upi.match(/^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/)) {
      errors.upi = "Enter a valid UPI ID";
      isValid = false;
    }
    setEditErrors(errors);
    return isValid;
  }, [editProfile]);

  const saveProfile = async () => {
    if (validateProfileFields()) {
      setIsLoading(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setProfile((prev) => ({
          ...prev,
          name: editProfile.name,
          phone: editProfile.phone,
          email: editProfile.email,
          upi: editProfile.upi,
          activityLog: [...prev.activityLog, { action: "Updated profile information", timestamp: new Date().toISOString() }],
        }));
        setIsEditing(false);
        setToastMessage("Profile updated!");
      } catch (error) {
        console.error("Error saving profile:", error);
        setToastMessage("Error updating profile.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const cancelEdit = () => {
    setEditProfile({ name: "", phone: "", email: "", upi: "" });
    setEditErrors({ name: "", phone: "", email: "", upi: "" });
    setIsEditing(false);
  };

  const filteredFavorites = useMemo(() => {
    const dishes = restaurantData
      ? Object.values(restaurantData)
          .flatMap((r) => r.menu || [])
          .filter((d) => profile.favorites.dishes.includes(d.id))
          .filter((d) => d.name.toLowerCase().includes(searchQuery.toLowerCase()))
      : [];
    const restaurants = profile.favorites.restaurants.filter((r) =>
      r.toLowerCase().includes(searchQuery.toLowerCase())
    );
    return { dishes, restaurants };
  }, [searchQuery, profile.favorites, restaurantData]);

  return (
    <ErrorBoundary>
      <AnimatePresence>
        {isOpen && (
          <div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-40"
              onClick={onClose}
              aria-hidden="true"
            />
            <motion.div
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 100 }}
              className={`fixed right-0 top-0 sm:right-6 sm:top-20 w-full sm:w-[28rem] max-h-screen sm:max-h-[85vh] overflow-y-auto z-50 rounded-none sm:rounded-xl shadow-2xl ${darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"}`}
              role="dialog"
              aria-labelledby="profile-modal-title"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <label htmlFor="avatar-upload" className="cursor-pointer relative">
                      <div className="w-16 h-16 rounded-full overflow-hidden border-4 border-green-500 shadow-lg hover:scale-105 transition-transform duration-300">
                        {uploading ? (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                            <div className="w-6 h-6 border-2 border-t-green-500 border-gray-200 rounded-full animate-spin" />
                          </div>
                        ) : (
                          <Image src={profile.avatar || "/zoomo-mascot.png"} alt="User avatar" className="w-full h-full object-cover" />
                        )}
                      </div>
                      <input
                        id="avatar-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleAvatarUpload}
                      />
                    </label>
                    <div>
                      <h2 id="profile-modal-title" className="text-2xl font-bold tracking-tight">
                        {profile.name || "Your Name"}
                      </h2>
                      <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                        {profile.email || "No email set"}
                      </p>
                    </div>
                  </div>
                  <motion.button
                    onClick={onClose}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className={`text-lg ${darkMode ? "text-gray-300 hover:text-gray-400" : "text-gray-600 hover:text-gray-900"}`}
                    aria-label="Close profile modal"
                  >
                    <FiX />
                  </motion.button>
                </div>
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-lg font-semibold">Profile Completion</h4>
                    <span className="text-sm text-green-500">{profileCompletion}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                    <div
                      className="bg-green-500 h-2.5 rounded-full"
                      style={{ width: `${profileCompletion}%` }}
                    ></div>
                  </div>
                </div>
                <div className="border-b border-gray-200 dark:border-gray-700 pb-4 mb-6">
                  <h4 className="text-lg font-semibold mb-3">Profile Stats</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Orders</p>
                      <p className="text-lg font-bold">{profile.orders.length}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Favorites</p>
                      <p className="text-lg font-bold">{favourites.length}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Points</p>
                      <p className="text-lg font-bold text-green-500">{profile.rewardPoints}</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    { id: "info", label: "Personal Info", icon: FiUser },
                    { id: "address", label: "Addresses", icon: FiMapPin },
                    { id: "payment", label: "Payment Methods", icon: FiCreditCard },
                    { id: "orders", label: "Order History", icon: FiFileText },
                    { id: "favorites", label: "Favorites", icon: FiHeart },
                    { id: "preferences", label: "Preferences", icon: FiSettings },
                  ].map((section) => (
                    <div key={section.id}>
                      <motion.button
                        onClick={() => toggleSection(section.id)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={`w-full flex items-center justify-between p-4 rounded-lg text-sm font-semibold transition-all duration-300 ${darkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-100 hover:bg-gray-200"}`}
                        aria-label={`Toggle ${section.label} section`}
                      >
                        <div className="flex items-center gap-3">
                          <section.icon className="text-green-500 text-xl" />
                          {section.label}
                        </div>
                        <span className="text-gray-500">{expandedSection === section.id ? "▼" : "▶"}</span>
                      </motion.button>
                      <AnimatePresence>
                        {expandedSection === section.id && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className={`p-4 rounded-lg mt-2 space-y-4 ${darkMode ? "bg-gray-700" : "bg-gray-100"}`}
                          >
                            {section.id === "info" && (
                              <div className="space-y-4">
                                {isEditing ? (
                                  <>
                                    <InputField
                                      label="Name"
                                      value={editProfile.name}
                                      onChange={(e) => setEditProfile({ ...editProfile, name: e.target.value })}
                                      error={editErrors.name}
                                      darkMode={darkMode}
                                      aria-label="Edit name"
                                    />
                                    <InputField
                                      label="Phone"
                                      value={editProfile.phone}
                                      onChange={(e) => setEditProfile({ ...editProfile, phone: e.target.value })}
                                      error={editErrors.phone}
                                      darkMode={darkMode}
                                      aria-label="Edit phone"
                                    />
                                    <InputField
                                      label="Email"
                                      type="email"
                                      value={editProfile.email}
                                      onChange={(e) => setEditProfile({ ...editProfile, email: e.target.value })}
                                      error={editErrors.email}
                                      darkMode={darkMode}
                                      aria-label="Edit email"
                                    />
                                    <InputField
                                      label="UPI ID"
                                      value={editProfile.upi}
                                      onChange={(e) => setEditProfile({ ...editProfile, upi: e.target.value })}
                                      error={editErrors.upi}
                                      darkMode={darkMode}
                                      aria-label="Edit UPI ID"
                                    />
                                    <div className="flex justify-end gap-3">
                                      <motion.button
                                        onClick={cancelEdit}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        className={`text-sm ${darkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"}`}
                                        aria-label="Cancel edit"
                                      >
                                        Cancel
                                      </motion.button>
                                      <motion.button
                                        onClick={saveProfile}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        className={`bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                                        disabled={isLoading}
                                        aria-label="Save profile"
                                      >
                                        {isLoading && <div className="w-4 h-4 border-2 border-t-white border-gray-200 rounded-full animate-spin" />}
                                        Save
                                      </motion.button>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <p className="text-sm">Name: {profile.name || "Not set"}</p>
                                    <p className="text-sm">Phone: {profile.phone || "Not set"}</p>
                                    <p className="text-sm">Email: {profile.email || "Not set"}</p>
                                    <p className="text-sm">UPI ID: {profile.upi || "Not set"}</p>
                                    <motion.button
                                      onClick={handleEditProfile}
                                      whileHover={{ scale: 1.02 }}
                                      whileTap={{ scale: 0.98 }}
                                      className="w-full bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold mt-3"
                                      aria-label="Edit profile"
                                    >
                                      Edit Profile
                                    </motion.button>
                                  </>
                                )}
                              </div>
                            )}
                            {section.id === "address" && (
                              <div className="space-y-4">
                                {profile.deliveryAddresses.length === 0 ? (
                                  <p className="text-sm text-gray-500 dark:text-gray-400">No addresses saved.</p>
                                ) : (
                                  profile.deliveryAddresses.map((address, i) => (
                                    <ListItem
                                      key={i}
                                      item={address}
                                      isDefault={profile.defaultAddress === address}
                                      onEdit={() => setEditAddress({ address, index: i })}
                                      onRemove={() =>
                                        confirmAction(
                                          () => removeDeliveryAddress(address),
                                          `Are you sure you want to remove this address: ${address}?`
                                        )
                                      }
                                      onSetDefault={() => setDefaultAddress(address)}
                                      darkMode={darkMode}
                                      type="address"
                                    />
                                  ))
                                )}
                                {editAddress.index !== null ? (
                                  <div className="space-y-3">
                                    <InputField
                                      label="Edit Address"
                                      value={editAddress.address}
                                      onChange={(e) => setEditAddress({ ...editAddress, address: e.target.value })}
                                      darkMode={darkMode}
                                      aria-label="Edit address"
                                    />
                                    <div className="flex justify-end gap-3">
                                      <motion.button
                                        onClick={() => setEditAddress({ address: "", index: null })}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        className={`text-sm ${darkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"}`}
                                        aria-label="Cancel address edit"
                                      >
                                        Cancel
                                      </motion.button>
                                      <motion.button
                                        onClick={editDeliveryAddress}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold"
                                        aria-label="Save address"
                                      >
                                        Save
                                      </motion.button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="space-y-3">
                                    <InputField
                                      label="New Address"
                                      value={newAddress}
                                      onChange={(e) => setNewAddress(e.target.value)}
                                      placeholder="Add new address..."
                                      darkMode={darkMode}
                                      aria-label="Add new address"
                                    />
                                    <motion.button
                                      onClick={addDeliveryAddress}
                                      whileHover={{ scale: 1.02 }}
                                      whileTap={{ scale: 0.98 }}
                                      className="w-full bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold"
                                      aria-label="Add address"
                                    >
                                      Add Address
                                    </motion.button>
                                  </div>
                                )}
                              </div>
                            )}
                            {section.id === "payment" && (
                              <div className="space-y-4">
                                {profile.paymentMethods.length === 0 ? (
                                  <p className="text-sm text-gray-500 dark:text-gray-400">No payment methods saved.</p>
                                ) : (
                                  profile.paymentMethods.map((method, i) => (
                                    <ListItem
                                      key={i}
                                      item={method}
                                      isDefault={profile.defaultPaymentMethod === method}
                                      onEdit={() => setEditPaymentMethod({ method, index: i })}
                                      onRemove={() =>
                                        confirmAction(
                                          () => removePaymentMethod(method),
                                          `Are you sure you want to remove this payment method: ${method}?`
                                        )
                                      }
                                      onSetDefault={() => setDefaultPaymentMethod(method)}
                                      darkMode={darkMode}
                                      type="payment method"
                                    />
                                  ))
                                )}
                                {editPaymentMethod.index !== null ? (
                                  <div className="space-y-3">
                                    <InputField
                                      label="Edit Payment Method"
                                      value={editPaymentMethod.method}
                                      onChange={(e) => setEditPaymentMethod({ ...editPaymentMethod, method: e.target.value })}
                                      darkMode={darkMode}
                                      aria-label="Edit payment method"
                                    />
                                    <div className="flex justify-end gap-3">
                                      <motion.button
                                        onClick={() => setEditPaymentMethod({ method: "", index: null })}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        className={`text-sm ${darkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"}`}
                                        aria-label="Cancel payment method edit"
                                      >
                                        Cancel
                                      </motion.button>
                                      <motion.button
                                        onClick={editPaymentMethodFn}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold"
                                        aria-label="Save payment method"
                                      >
                                        Save
                                      </motion.button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="space-y-3">
                                    <InputField
                                      label="New Payment Method"
                                      value={newPaymentMethod}
                                      onChange={(e) => setNewPaymentMethod(e.target.value)}
                                      placeholder="Add new payment method..."
                                      darkMode={darkMode}
                                      aria-label="Add new payment method"
                                    />
                                    <motion.button
                                      onClick={addPaymentMethod}
                                      whileHover={{ scale: 1.02 }}
                                      whileTap={{ scale: 0.98 }}
                                      className="w-full bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold"
                                      aria-label="Add payment method"
                                    >
                                      Add Payment Method
                                    </motion.button>
                                  </div>
                                )}
                              </div>
                            )}
                            {section.id === "orders" && (
                              <div className="space-y-4">
                                {profile.orders.length === 0 ? (
                                  <p className="text-sm text-gray-500 dark:text-gray-400">No orders yet.</p>
                                ) : (
                                  <div className="space-y-3 max-h-48 overflow-y-auto custom-scrollbar">
                                    {profile.orders.map((order, i) => (
                                      <motion.div
                                        key={i}
                                        className="p-3 rounded-lg bg-gray-100 dark:bg-gray-600 text-sm cursor-pointer"
                                        whileHover={{ scale: 1.02 }}
                                        onClick={() => safeNavigate(`/order/${order.id}`)}
                                      >
                                        <p className="font-semibold">Order #{order.id}</p>
                                        <p className="text-xs">Date: {new Date(order.date).toLocaleDateString()}</p>
                                        <p className="text-xs">Total: ₹{order.total.toFixed(2)}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                          Items: {order.items?.map((item) => item.name).join(", ") || "N/A"}
                                        </p>
                                      </motion.div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                            {section.id === "favorites" && (
                              <div className="space-y-4">
                                <div className="relative">
                                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                  <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search favorites..."
                                    className={`w-full pl-10 pr-3 py-2 rounded-lg border focus:ring-2 focus:ring-green-500 text-sm ${darkMode ? "bg-gray-700 text-white border-gray-600" : "bg-gray-100 text-gray-900 border-gray-300"}`}
                                    aria-label="Search favorites"
                                  />
                                </div>
                                <h5 className="text-sm font-semibold">Favorite Restaurants</h5>
                                {filteredFavorites.restaurants.length === 0 ? (
                                  <p className="text-sm text-gray-500 dark:text-gray-400">No favorite restaurants.</p>
                                ) : (
                                  <div className="space-y-2">
                                    {filteredFavorites.restaurants.map((restaurant, i) => (
                                      <div key={i} className="flex items-center justify-between text-sm">
                                        <span>{restaurant}</span>
                                        <motion.button
                                          onClick={() => addFavoriteRestaurant(restaurant)}
                                          whileHover={{ scale: 1.1 }}
                                          whileTap={{ scale: 0.9 }}
                                          className="text-red-600 hover:text-red-700"
                                          aria-label={`Remove ${restaurant} from favorites`}
                                        >
                                          Remove
                                        </motion.button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                <h5 className="text-sm font-semibold mt-4">Favorite Dishes</h5>
                                {filteredFavorites.dishes.length === 0 ? (
                                  <p className="text-sm text-gray-500 dark:text-gray-400">No favorite dishes.</p>
                                ) : (
                                  <div className="space-y-2">
                                    {filteredFavorites.dishes.map((dish, i) => (
                                      <div
                                        key={i}
                                        className="flex items-center justify-between text-sm p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                                      >
                                        <div className="flex items-center gap-2">
                                          <Image src={dish.img} alt={dish.name} className="w-8 h-8 rounded-md object-cover" />
                                          <span>{dish.name}</span>
                                        </div>
                                        <div className="flex gap-2">
                                          <motion.button
                                            onClick={() => reorderDish(dish)}
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.9 }}
                                            className="text-green-500 hover:text-green-600"
                                            aria-label={`Reorder ${dish.name}`}
                                          >
                                            <FiShoppingCart />
                                          </motion.button>
                                          <motion.button
                                            onClick={() => addFavoriteDish(dish)}
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.9 }}
                                            className="text-red-600 hover:text-red-700"
                                            aria-label={`Remove ${dish.name} from favorites`}
                                          >
                                            Remove
                                          </motion.button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                            {section.id === "preferences" && (
                              <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                  <label className="text-sm">Notifications</label>
                                  <input
                                    type="checkbox"
                                    checked={notificationToggle}
                                    onChange={handleNotificationToggle}
                                    className="h-4 w-4 text-green-500 focus:ring-green-500 rounded"
                                    aria-label="Toggle notifications"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-1">Dietary Preference</label>
                                  <select
                                    value={dietaryPreference}
                                    onChange={handleDietaryChange}
                                    className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-green-500 text-sm ${darkMode ? "bg-gray-700 text-white border-gray-600" : "bg-gray-100 text-gray-900 border-gray-300"}`}
                                    aria-label="Select dietary preference"
                                  >
                                    <option value="None">None</option>
                                    <option value="Vegetarian">Vegetarian</option>
                                    <option value="Vegan">Vegan</option>
                                    <option value="Gluten-Free">Gluten-Free</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-1">Language</label>
                                  <select
                                    value={languagePreference}
                                    onChange={handleLanguageChange}
                                    className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-green-500 text-sm ${darkMode ? "bg-gray-700 text-white border-gray-600" : "bg-gray-100 text-gray-900 border-gray-300"}`}
                                    aria-label="Select language"
                                  >
                                    <option value="English">English</option>
                                    <option value="Hindi">Hindi</option>
                                    <option value="Spanish">Spanish</option>
                                  </select>
                                </div>
                              </div>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                  <motion.button
                    onClick={() =>
                      confirmAction(logout, "Are you sure you want to sign out? All unsaved changes will be lost.")
                    }
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold mt-6 transition-all shadow-sm hover:shadow-md"
                    aria-label="Sign out"
                  >
                    Sign Out
                  </motion.button>
                </div>
              </div>
            </motion.div>
            {showConfirm.show && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
              >
                <motion.div
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0.8 }}
                  className={`p-6 rounded-lg ${darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"} shadow-xl`}
                >
                  <p className="text-sm mb-4">{showConfirm.message}</p>
                  <div className="flex justify-end gap-3">
                    <motion.button
                      onClick={() => setShowConfirm({ show: false, action: null, message: "" })}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`text-sm ${darkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"}`}
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      onClick={handleConfirm}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold"
                    >
                      Confirm
                    </motion.button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </div>
        )}
      </AnimatePresence>
    </ErrorBoundary>
  );
};

export default ProfileModal;